import { useMemo, useState, useEffect, useCallback } from "react";

import { POKE_DATA } from "game/data/pokemon/data";
import { POKEMON_SKILLS } from "game/data/skills/POKEMON_SKILLS";
import { ExpUtils } from "utils/pokemon/exp-utils";

import { Alert } from "@/components/alert/alert";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { PokemonTalent } from "@/components/pokemon/pokemon-talent";
import { PokemonTypeBadge } from "@/components/pokemon/pokemon-type-badge";
import { Button } from "@/components/ui/buttons/button";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { EvolutionSelectDialog } from "@/components/ui/dialog/evolution-select-dialog";
import { ReleaseConfirmDialog } from "@/components/ui/dialog/release-confirm-dialog";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { EvolutionAnimationScreen } from "@/components/ui/overlays/evolution-animation-screen";
import { SegmentedProgressBar } from "@/components/ui/progress/segmented-progress-bar";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { useBackAction } from "@/hooks/use-back-action";
import { PencilIcon, CheckIcon } from "@/icons";
import { useAuth } from "@/providers/auth-provider";
import { useSPAContext } from "@/providers/spa-provider";
import {
  getEvolutionOptions,
  evolvePokemon,
} from "@/services/pokemon/evolve-pokemon";
import { releasePokemon } from "@/services/pokemon/release-pokemon";
import { updatePokemonName } from "@/services/pokemon/update-pokemon-name";
import { updatePokemonSkills } from "@/services/pokemon/update-pokemon-skills";
import { getPokemonStats } from "@/utils/pokemon-stats-utils";
import { isXsScreen } from "@/utils/screen-size-check";
import { purityToStatBonus, calculatePurity } from "@/utils/talent-utils";

import { NoPokemonFallback } from "./no-pokemon-fallback";
import { SkillSection } from "./skill-section";

import type { PokeNames } from "types/pokemon/poke-names";
import type { EvolutionOptionInfo } from "types/web-app";
import { SkillData } from "types/battle-engine/battle";

export const PokemonInfoView = () => {
  const {
    selectedPokemons,
    setCurrentRoute,
    previousRoute,
    setSelectedPokemons,
  } = useSPAContext();
  const { getAccessToken } = useAuth();
  const handleBack = useCallback(() => {
    setCurrentRoute(previousRoute);
  }, [previousRoute, setCurrentRoute]);

  useBackAction(handleBack);

  const pokemon = selectedPokemons[0];

  const [isEditingSkills, setIsEditingSkills] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSubmittingSkillChanges, setIsSubmittingSkillChanges] =
    useState(false);
  const [showTalentReport, setShowTalentReport] = useState(false);
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  // Evolution state
  const [showEvolutionDialog, setShowEvolutionDialog] = useState(false);
  const [evolutionOptions, setEvolutionOptions] = useState<
    EvolutionOptionInfo[]
  >([]);
  const [isLoadingEvolutions, setIsLoadingEvolutions] = useState(false);
  const [isEvolving, setIsEvolving] = useState(false);

  // Animation state
  const [showEvolutionAnimation, setShowEvolutionAnimation] = useState(false);
  const [preEvolutionName, setPreEvolutionName] = useState("");
  const [targetEvolutionName, setTargetEvolutionName] = useState("");
  const [pendingEvolutionData, setPendingEvolutionData] = useState<any>(null);
  const [preEvolutionStats, setPreEvolutionStats] = useState<any>(null);
  const [targetEvolutionStats, setTargetEvolutionStats] = useState<any>(null);

  // Initialize selectedSkills when entering edit mode
  useEffect(() => {
    if (isEditingSkills && pokemon) {
      setSelectedSkills([...pokemon.active_skills]);
    }
  }, [isEditingSkills, pokemon]);

  // Initialize editingName when entering name edit mode
  useEffect(() => {
    if (isEditingName && pokemon) {
      setEditingName(pokemon.name || "");
    }
  }, [isEditingName, pokemon]);

  const skills = useMemo(() => {
    if (!pokemon) return [];

    return pokemon.active_skills
      .map((id) => id.replace(/_/g, "-"))
      .map((skill) => POKEMON_SKILLS[skill]);
  }, [pokemon]);

  const knownSkills = useMemo(() => {
    if (!pokemon) return [];

    return pokemon.known_skills
      .map((id) => id.replace(/_/g, "-"))
      .map((skill) => POKEMON_SKILLS[skill]);
  }, [pokemon]);

  const maxSelections = Math.min(3, pokemon?.known_skills.length || 0);

  const pokemonTypes = useMemo(() => {
    if (!pokemon) return [];
    const pokeName = pokemon.base_pokemon_name as PokeNames;
    const pokeBaseData = POKE_DATA[pokeName];

    return (
      pokeBaseData?.types
        .filter((type) => type !== undefined)
        .map((type) => String(type)) || []
    );
  }, [pokemon]);

  const purity = useMemo(() => {
    if (!pokemon || !pokemonTypes.length) return 0;
    return calculatePurity(pokemon.talents.map(String), pokemonTypes);
  }, [pokemon, pokemonTypes]);

  const statBonusPercentage = useMemo(() => {
    return purityToStatBonus(purity);
  }, [purity]);

  const purityRatio = useMemo(() => {
    return purity / 9; // Max purity is 9
  }, [purity]);

  const pokemonStats = useMemo(() => {
    return pokemon ? getPokemonStats(pokemon, 1) : null;
  }, [pokemon]);

  const handleEditClick = () => {
    if (pokemon && pokemon.known_skills.length > 0) {
      setIsEditingSkills(true);
      setSelectedSkills([...pokemon.active_skills]);
    }
  };

  const handleNameEditClick = () => {
    setIsEditingName(true);
  };

  const handleNameCancel = () => {
    setIsEditingName(false);
    setEditingName("");
  };

  const handleNameSave = async () => {
    if (!pokemon) return;

    const trimmedName = editingName.trim();
    const finalName = trimmedName === "" ? null : trimmedName;

    // Check if name actually changed
    const currentName = pokemon.name || "";
    if (
      (finalName === null && currentName === "") ||
      finalName === currentName
    ) {
      setIsEditingName(false);
      setEditingName("");
      return;
    }

    setIsUpdatingName(true);

    if (!getAccessToken()) {
      alert("Authentication error. Please try again");
      setIsUpdatingName(false);
      return;
    }

    try {
      const result = await updatePokemonName({
        pokemon_id: pokemon.id,
        name: finalName,
      });

      if (result.ok) {
        // Update local pokemon data
        pokemon.name = finalName;

        // Update the pokemon in the SPA context to persist changes
        setSelectedPokemons((prevPokemons) =>
          prevPokemons.map((p) =>
            p.id === pokemon.id ? { ...p, name: finalName } : p,
          ),
        );

        setIsEditingName(false);
        setEditingName("");
      } else {
        alert(result.message || "Failed to update Pokemon name");
      }
    } catch (error) {
      console.error(error);
      alert("Failed to update Pokemon name");
    } finally {
      setIsUpdatingName(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingSkills(false);
    setSelectedSkills([]);
    setHasUnsavedChanges(false);
  };

  const checkForChanges = (newSelection: string[]) => {
    if (!pokemon) return;
    const originalSkills = pokemon.active_skills.sort().join(",");
    const newSkills = newSelection.sort().join(",");
    setHasUnsavedChanges(originalSkills !== newSkills);
  };

  const handleSkillToggle = (skillId: string) => {
    if (!isEditingSkills || !pokemon) return;

    // Convert skill ID (underscore format) to database format (dash format)
    const skillDbId = skillId.replace(/_/g, "-");
    const isSelected = selectedSkills.includes(skillDbId);
    const currentCount = selectedSkills.length;

    if (isSelected) {
      const newSelected = selectedSkills.filter((id) => id !== skillDbId);
      setSelectedSkills(newSelected);
      checkForChanges(newSelected);
    } else {
      let newSelected: string[];

      if (currentCount < maxSelections) {
        // Room available, just add the skill
        newSelected = [...selectedSkills, skillDbId];
      } else {
        // At max capacity, auto-deselect the first skill and add the new one
        newSelected = [...selectedSkills.slice(1), skillDbId];
      }

      setSelectedSkills(newSelected);
      checkForChanges(newSelected);
    }
  };

  const handleConfirm = async () => {
    if (!pokemon || !hasUnsavedChanges) return;

    setIsSubmittingSkillChanges(true);

    if (!getAccessToken()) {
      alert("Authentication error. Please try again");
      setIsSubmittingSkillChanges(false);
      return;
    }

    try {
      const result = await updatePokemonSkills({
        pokemon_id: pokemon.id,
        active_skills: selectedSkills,
      });

      if (result.ok) {
        // Update local pokemon data
        pokemon.active_skills = selectedSkills;

        // Update the pokemon in the SPA context to persist changes
        setSelectedPokemons((prevPokemons) =>
          prevPokemons.map((p) =>
            p.id === pokemon.id ? { ...p, active_skills: selectedSkills } : p,
          ),
        );

        setHasUnsavedChanges(false);
        setIsEditingSkills(false);
        setSelectedSkills([]);
      } else {
        alert(result.message || "Failed to update skills");
      }
    } catch (error) {
      console.error(error);
      alert("Failed to update skills");
    } finally {
      setIsSubmittingSkillChanges(false);
    }
  };

  const handleRelease = async () => {
    if (!pokemon) return;

    if (!getAccessToken()) {
      alert("Authentication error. Please try again");
      return;
    }

    setIsReleasing(true);

    try {
      const result = await releasePokemon({
        pokemon_id: pokemon.id,
      });

      if (result.ok) {
        // Remove pokemon from local list and go back
        setSelectedPokemons((prev) => prev.filter((p) => p.id !== pokemon.id));
        setCurrentRoute(previousRoute);
      } else {
        alert(result.message || "Failed to release pokemon");
      }
    } catch (error) {
      console.error(error);
      alert("Failed to release pokemon");
    } finally {
      setIsReleasing(false);
      setShowReleaseConfirm(false);
    }
  };

  /**
   * Check if Pokemon can evolve and show evolution dialog
   */
  const handleEvolveClick = async () => {
    if (!pokemon) return;

    if (!getAccessToken()) {
      Alert("Authentication error. Please try again");
      return;
    }

    setIsLoadingEvolutions(true);

    try {
      const result = await getEvolutionOptions({
        pokemon_id: pokemon.id,
      });

      if (result.ok && result.data) {
        setEvolutionOptions(result.data.evolutions);
        setShowEvolutionDialog(true);
      } else {
        Alert(result.message || "Failed to get evolution options");
      }
    } catch (error) {
      console.error(error);
      Alert("Failed to check evolution options");
    } finally {
      setIsLoadingEvolutions(false);
    }
  };

  /**
   * Execute evolution to selected target species
   */
  const handleEvolutionSelect = async (targetSpecies: string) => {
    if (!pokemon) return;

    if (!getAccessToken()) {
      Alert("Authentication error. Please try again");
      return;
    }

    setIsEvolving(true);

    try {
      const result = await evolvePokemon({
        pokemon_id: pokemon.id,
        target_species: targetSpecies,
      });

      if (result.ok && result.data?.pokemon) {
        // Prepare animation
        setPreEvolutionName(pokemon.base_pokemon_name);
        setTargetEvolutionName(result.data.pokemon.base_pokemon_name);
        setPendingEvolutionData(result.data.pokemon);

        // Calculate stats for animation
        const oldStats = getPokemonStats({ ...pokemon, level: 1 });

        const newPokemon = {
          ...result.data.pokemon,
          talents: pokemon.talents,
          name: pokemon.name,
          state: pokemon.state,
          experience: pokemon.experience,
          created_at: pokemon.created_at,
          pokemon_children: pokemon.pokemon_children,
          pokemon_parents: pokemon.pokemon_parents,
        };
        const newStats = getPokemonStats({ ...newPokemon, level: 1 });
        setPreEvolutionStats(oldStats);
        setTargetEvolutionStats(newStats);

        setShowEvolutionDialog(false);
        setEvolutionOptions([]);

        // Start animation
        setShowEvolutionAnimation(true);
      } else {
        Alert(result.message || "Failed to evolve Pokemon");
      }
    } catch (error) {
      console.error(error);
      Alert("Failed to evolve Pokemon");
    } finally {
      setIsEvolving(false);
    }
  };

  const handleAnimationComplete = () => {
    if (!pokemon || !pendingEvolutionData) {
      setShowEvolutionAnimation(false);
      return;
    }

    // Update local pokemon data
    const updatedPokemon = {
      ...pokemon,
      base_pokemon_name: pendingEvolutionData.base_pokemon_name,
      known_skills: pendingEvolutionData.known_skills,
      active_skills: pendingEvolutionData.active_skills,
    };

    setSelectedPokemons((prevPokemons) =>
      prevPokemons.map((p) => (p.id === pokemon.id ? updatedPokemon : p)),
    );

    setShowEvolutionAnimation(false);
    setPendingEvolutionData(null);
  };

  const experienceRatio = useMemo(() => {
    if (!pokemon) return 0;

    const currentLevelExperience =
      pokemon.level === 1 ? 0 : ExpUtils.expForLevel(pokemon.level);
    const nextLevelExperience = ExpUtils.expForLevel(pokemon.level + 1);

    const ratio =
      (pokemon.experience - currentLevelExperience) /
      (nextLevelExperience - currentLevelExperience);

    return ratio;
  }, [pokemon?.experience, pokemon?.level]);

  if (!pokemon) return <NoPokemonFallback />;

  return (
    <div className="bg-black/50 backdrop-blur-[5px] p-1 w-full h-full overflow-x-hidden">
      <StandardFrame
        className="shadow-cartoon w-full h-full max-h-[calc(100vh-2rem)] overflow-visible overflow-x-hidden"
        variant="black"
      >
        <div className="z-40 flex flex-col w-full h-full overflow-x-hidden overflow-y-auto">
          <div className="flex justify-between items-center bg-pk-gray-2 px-3 pt-1 w-full">
            {isEditingName ? (
              <div className="flex flex-1 items-center gap-2 min-w-0">
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleNameSave();
                    } else if (e.key === "Escape") {
                      handleNameCancel();
                    }
                  }}
                  className="flex-1 bg-transparent px-2 py-1 border-2 border-pk-garnet-3 focus:border-pk-garnet-4 rounded focus:outline-none min-w-0 font-bold text-pk-white text-3xl placeholder-pk-gray-3"
                  placeholder={pokemon.base_pokemon_name}
                  maxLength={50}
                  disabled={isUpdatingName}
                  autoFocus
                />
                <div className="flex items-center gap-2">
                  <SimpleButton
                    variant="garnet"
                    className="px-2 py-1"
                    onClick={handleNameSave}
                    loading={isUpdatingName}
                    disabled={editingName.length > 50}
                  >
                    <CheckIcon width={20} height={20} className="invert" />
                  </SimpleButton>
                  <SimpleButton
                    variant="garnet"
                    className="px-2 py-1"
                    onClick={handleNameCancel}
                    disabled={isUpdatingName}
                  >
                    ×
                  </SimpleButton>
                </div>
              </div>
            ) : (
              <>
                <div
                  className="hover:opacity-80 transition-opacity cursor-pointer"
                  onClick={handleNameEditClick}
                  title="Click to edit name"
                >
                  <StrokedText
                    fontSize={isXsScreen() ? 18 : 32}
                    strokeWidth={8}
                  >
                    {`${pokemon.name ?? pokemon.base_pokemon_name}`}
                  </StrokedText>
                </div>
                <button onClick={handleNameEditClick}>
                  <PencilIcon
                    width={isXsScreen() ? 20 : 32}
                    height={isXsScreen() ? 20 : 32}
                    className="opacity-50 hover:opacity-80 transition-opacity cursor-pointer"
                  />
                </button>
              </>
            )}
          </div>
          <div className="h-0 overflow-visible font-bold text-pk-gray-1 text-3xl">
            #{pokemon.display_id}
          </div>
          <div className="relative justify-between items-center gap-6 grid grid-cols-5">
            {/* Left Side - Pokemon Info */}
            <div className="z-10 relative flex flex-col items-start gap-2 col-span-3 p-2 h-full overflow-visible grow">
              <div className="relative flex justify-center items-center mt-auto w-full h-[150px] translate-y-[50px]">
                <PokemonSprite
                  pokemon={pokemon}
                  style={{ scale: 3.5 }}
                  className="z-20"
                  showShadow={true}
                />
              </div>

              <div className="flex flex-col mt-auto w-full">
                <StrokedText fontSize={24} strokeWidth={8}>
                  {`Level ${pokemon.level.toString()}`}
                </StrokedText>

                <div className="flex flex-col items-start gap-0 w-full">
                  <StrokedText
                    fontSize={14}
                    strokeWidth={8}
                    height={1}
                    className="z-10 translate-y-2"
                  >
                    Experience
                  </StrokedText>
                  <SegmentedProgressBar
                    ratio={experienceRatio}
                    variant="green"
                  />
                </div>

                {/* Type Badges */}
                {pokemonTypes.length > 0 && (
                  <div className="gap-2 grid grid-cols-2 mt-2">
                    {pokemonTypes.map((type, index) => (
                      <PokemonTypeBadge
                        key={`type-${index}`}
                        type={type}
                        iconSize={18}
                        hideIcon={isXsScreen()}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Pokemon Info */}
            <div className="z-10 relative flex flex-col items-start gap-8 col-span-2 mt-2 pb-2.5 h-full overflow-visible grow">
              <div className="flex flex-col gap-6 bg-pk-gray-2 mt-auto p-2 rounded-sm w-full">
                <div className="flex flex-col items-start gap-0 w-full">
                  <StrokedText
                    fontSize={isXsScreen() ? 10 : 18}
                    strokeWidth={8}
                    height={1}
                    className="z-10"
                  >
                    Ability Power
                  </StrokedText>
                  <SegmentedProgressBar
                    ratio={(pokemonStats?.ability_power || 0) / 250}
                    variant="red"
                    maxSquares={isXsScreen() ? 8 : 12}
                    segmented={false}
                  />

                  <div className="mt-1 text-pk-white text-xs">
                    {pokemonStats?.ability_power || 0}
                  </div>
                </div>

                <div className="flex flex-col items-start gap-0 w-full">
                  <StrokedText
                    fontSize={isXsScreen() ? 10 : 18}
                    strokeWidth={8}
                    height={1}
                    className="z-10"
                  >
                    Resistance
                  </StrokedText>
                  <SegmentedProgressBar
                    ratio={(pokemonStats?.resistance || 0) / 250}
                    variant="blue"
                    maxSquares={isXsScreen() ? 8 : 12}
                    segmented={false}
                  />
                  <div className="mt-1 text-pk-white text-xs">
                    {pokemonStats?.resistance || 0}
                  </div>
                </div>

                <div className="flex flex-col items-start gap-0 w-full">
                  <StrokedText
                    fontSize={isXsScreen() ? 10 : 18}
                    strokeWidth={8}
                    height={1}
                    className="z-10"
                  >
                    Dexterity
                  </StrokedText>
                  <SegmentedProgressBar
                    ratio={(pokemonStats?.speed || 0) / 250}
                    variant="yellow"
                    maxSquares={isXsScreen() ? 8 : 12}
                    segmented={false}
                  />
                  <div className="mt-1 text-pk-white text-xs">
                    {pokemonStats?.speed || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Middle Section */}
          <div className="gap-2 grid grid-cols-6 mt-2">
            {/* Talent Slots */}
            <div
              className="flex flex-col col-span-2"
              onClick={() => setShowTalentReport(!showTalentReport)}
              onMouseEnter={() => setShowTalentReport(true)}
              onMouseLeave={() => setShowTalentReport(false)}
            >
              <div
                className="bg-pk-garnet-2 hover:bg-pk-garnet-3 px-2 rounded-tr-lg w-max font-bold text-pk-gray-4 transition-colors cursor-pointer"
                title="Click to toggle talent view"
              >
                Talents
              </div>
              {showTalentReport ? (
                <div className="space-y-1 bg-pk-garnet-2 p-4 rounded-sm rounded-tl-none h-40">
                  {/* Purity Progress Bar */}
                  <div className="space-y-2">
                    <div className="text-center">
                      <StrokedText
                        fontSize={14}
                        strokeWidth={4}
                        className="text-pk-white"
                      >
                        Purity ({purity.toString()}/9)
                      </StrokedText>
                    </div>
                    <SegmentedProgressBar
                      ratio={purityRatio}
                      variant={
                        purity > 6 ? "green" : purity > 4 ? "blue" : "orange"
                      }
                      maxSquares={9}
                      className="h-4"
                    />
                  </div>

                  {/* Stat Bonus */}
                  <div className="space-y-1 text-center">
                    <StrokedText
                      fontSize={12}
                      strokeWidth={4}
                      className="text-pk-yellow-2"
                    >
                      Stat Bonus
                    </StrokedText>
                    <div className="inline-block bg-gradient-to-r from-pk-yellow-3 to-pk-yellow-2 px-2 py-1 rounded-lg">
                      <StrokedText
                        fontSize={14}
                        strokeWidth={6}
                        className="text-pk-black"
                      >
                        +{statBonusPercentage.toFixed(1)}%
                      </StrokedText>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="gap-1 grid grid-cols-3 bg-pk-garnet-2 p-2 rounded-sm rounded-tl-none w-full">
                  {pokemon.talents.map((talent, index) => (
                    <PokemonTalent
                      key={`talent-${index}`}
                      talent={talent.toString()}
                      width={20}
                      height={24}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Items */}
            <div className="flex flex-col col-span-4">
              <div className="bg-pk-garnet-2 px-2 rounded-tr-lg w-max font-bold text-pk-gray-4">
                Items
              </div>
              <div className="relative gap-2 grid grid-cols-2 bg-pk-garnet-2 p-2 rounded-sm rounded-tl-none w-full h-full">
                <div className="top-0 left-0 z-20 absolute inset-0 flex justify-center items-center bg-black/50 backdrop-blur">
                  COMING SOON
                </div>
                <div className="flex justify-center items-center bg-pk-garnet-1 rounded-lg w-full h-full">
                  <span className="opacity-50 text-xl">+</span>
                </div>
                <div className="flex justify-center items-center bg-pk-garnet-1 rounded-lg w-full h-full">
                  <span className="opacity-50 text-xl">+</span>
                </div>
              </div>
            </div>
          </div>

          {/* Skill Cards */}
          {pokemon.known_skills.length > 0 && (
            <SkillSection
              isEditingSkills={isEditingSkills}
              handleSkillToggle={handleSkillToggle}
              handleEditClick={handleEditClick}
              handleConfirm={handleConfirm}
              handleCancelEdit={handleCancelEdit}
              isSubmittingSkillChanges={isSubmittingSkillChanges}
              skills={
                skills.filter((skill) => skill !== undefined) as SkillData[]
              }
              knownSkills={
                knownSkills.filter(
                  (skill) => skill !== undefined,
                ) as SkillData[]
              }
              selectedSkills={selectedSkills}
            />
          )}

          {/* Cancel / Confirm Button */}
          <div className="items-center gap-2 grid grid-cols-3 mt-8 mb-1 w-full">
            <div className=""></div>

            {!isEditingSkills && !hasUnsavedChanges && (
              <div className="gap-2 grid grid-cols-2 col-span-2">
                <Button
                  variant="blue"
                  onClick={handleEvolveClick}
                  loading={isLoadingEvolutions}
                  className="w-full"
                >
                  Evolve
                </Button>
                <Button
                  variant="garnet"
                  className="bg-pk-red-4 text-white"
                  onClick={() => setShowReleaseConfirm(true)}
                >
                  <span className="text-sm xs:text-base">Release</span>
                </Button>
              </div>
            )}

            {hasUnsavedChanges && (
              <Button variant="garnet" onClick={handleConfirm}>
                <span className="">CONFIRM</span>
              </Button>
            )}
          </div>
        </div>
      </StandardFrame>

      <ReleaseConfirmDialog
        isOpen={showReleaseConfirm}
        onClose={() => setShowReleaseConfirm(false)}
        onConfirm={handleRelease}
        pokemon={pokemon}
        isLoading={isReleasing}
      />

      <EvolutionSelectDialog
        isOpen={showEvolutionDialog}
        onClose={() => {
          setShowEvolutionDialog(false);
          setEvolutionOptions([]);
        }}
        onSelect={handleEvolutionSelect}
        pokemonName={pokemon?.name ?? pokemon?.base_pokemon_name ?? ""}
        pokemonBaseName={pokemon?.base_pokemon_name ?? ""}
        evolutions={evolutionOptions}
        isLoading={isEvolving}
      />

      <EvolutionAnimationScreen
        isOpen={showEvolutionAnimation}
        fromPokemonName={preEvolutionName}
        toPokemonName={targetEvolutionName}
        oldStats={
          preEvolutionStats ?? { ability_power: 0, resistance: 0, speed: 0 }
        }
        newStats={
          targetEvolutionStats ?? { ability_power: 0, resistance: 0, speed: 0 }
        }
        onComplete={handleAnimationComplete}
      />
    </div>
  );
};
